import React, { FC, forwardRef, useMemo } from 'react';
import hoistNonReactStatics from 'hoist-non-react-statics';

import USPostalCodeValidator from 'ia-react-core/~/validators/USPostalCodeValidator';
import stateToValueUSPostalCodeFormatter from 'ia-react-core/~/formatters/stateToValueUSPostalCodeFormatter';
import valuetoStateUSPostalCodeFormatter from 'ia-react-core/~/formatters/valuetoStateUSPostalCodeFormatter';
import { ValidatorMap } from 'ia-react-core/~/interfaces/ValidatorMap';
import requiredValidator from '../../../validators/requiredValidator';
import filterPropsByComponent from '../../../utils/components/_filterPropsByComponent';
import Control from '../Control';
import { USPostalCodeControlComposedProps, USPostalCodeControlDefaultProps } from './USPostalCodeControl';

const withControl = (WrappedComponent: FC<USPostalCodeControlComposedProps>) => {
  const WithControl = forwardRef<HTMLInputElement, USPostalCodeControlComposedProps>(({
    children, readOnly, textOnly, required, recommended, validators, stateToValueFormatters, valueToStateFormatters, ...props
  }, ref) => {
    const defaultValidators = useMemo(() => ({
      required: requiredValidator,
      recommended: { validator: requiredValidator, type: 'warning' },
    }), []);

    const resolvedValidators: ValidatorMap<string> = useMemo(() => ({
      postalCode: USPostalCodeValidator,
      ...validators,
    }), [validators]);

    const resolvedStateToValueFormatters = useMemo(() => [
      {
        formatter: stateToValueUSPostalCodeFormatter,
        deferred: true,
      },
      ...stateToValueFormatters,
    ], [stateToValueFormatters]);

    const resolvedValueToStateFormatters = useMemo(() => [
      // Temporary display formatter while invalid
      stateToValueUSPostalCodeFormatter,
      // Final formatter when valid
      {
        formatter: valuetoStateUSPostalCodeFormatter,
        options: { forceSeparator: true },
        deferred: true,
      },
      ...valueToStateFormatters,
    ], [valueToStateFormatters]);

    const TypedControl = Control<string, string, HTMLInputElement, { required?: boolean; recommended?: boolean }>;

    return (
      <TypedControl
        {...filterPropsByComponent(props, TypedControl)}
        stateToValueFormatters={resolvedStateToValueFormatters}
        valueToStateFormatters={resolvedValueToStateFormatters}
        required={required}
        recommended={recommended}
        defaultValidators={defaultValidators}
        validators={resolvedValidators}
        readOnly={readOnly}
        textOnly={textOnly}
      >
        {({
          id, value, isValid, hasWarning, hasCaution, hasInfo, isTouched, inputRef, errorMessagesId, onChange: onChangeCallback, onBlur: onBlurCallback, onKeyDown: onKeyDownCallback,
        }) => (
          <WrappedComponent
            id={id}
            data-control
            {...props}
            value={value}
            isValid={isValid}
            hasWarning={hasWarning}
            hasCaution={hasCaution}
            isTouched={isTouched}
            hasInfo={hasInfo}
            inputRef={inputRef}
            errorMessagesId={errorMessagesId}
            onChangeCallback={onChangeCallback}
            onBlurCallback={onBlurCallback}
            onKeyDownCallback={onKeyDownCallback}
            readOnly={readOnly}
            textOnly={textOnly}
            required={required}
            ref={ref}
          >
            {children}
          </WrappedComponent>
        )}
      </TypedControl>
    );
  }) as FC<USPostalCodeControlComposedProps>;

  WithControl.defaultProps = USPostalCodeControlDefaultProps;

  return hoistNonReactStatics(WithControl, WrappedComponent);
};

export default withControl;
